/**
 * Which chrome the auth gate should render.
 *
 * Extracted so a refetch of `/api/admin/me` cannot tear the shell down: once
 * we have data, the console stays mounted and only the page <Outlet /> swaps.
 *
 * THE GATE DOES NOT BLOCK ON ITS OWN ROUND TRIP. It used to hold the <Outlet />
 * back until `/api/admin/me` answered, which made every cold load two SERIAL
 * API round trips — the gate's, each carrying its own session and role hops,
 * and only then the page's. The shell renders while that query is in flight so
 * the page's read starts in the same tick. The cost is that an unauthenticated
 * visitor sees console chrome for one round trip before landing on sign-in: a
 * presentation trade, not a security one, since every request re-derives
 * authorization server-side regardless of what the SPA chose to draw.
 *
 * That makes the OTHER input load-bearing. With the shell up early, a page's
 * own query can be the first to learn there is no session, so a 401 from
 * anywhere is the same verdict as a 401 from the gate (see
 * `lib/unauthorized.ts`) — without it the console would answer an expired
 * session with a screen full of page errors and no way to sign in.
 *
 * There is deliberately no client-side "do we have a credential" input here.
 * A browser session is an HttpOnly cookie the SPA cannot read, so an empty
 * sessionStorage says nothing about whether the caller is signed in — only the
 * server knows. Gating on a stored token would show the sign-in screen to an
 * already-signed-in operator and never ask the server at all.
 */
export type GatePhase = "signin" | "error" | "shell";

export function gatePhase(input: {
  isPending: boolean;
  isError: boolean;
  errorStatus?: number;
  hasData: boolean;
  /** Any query in the cache is holding a 401 — the session is gone. */
  pageUnauthorized?: boolean;
}): GatePhase {
  if (input.isError && input.errorStatus === 401) return "signin";
  if (input.pageUnauthorized) return "signin";
  if (input.isError) return "error";
  if (input.hasData) return "shell";
  // Pending means "not yet ruled on", never "signed out". Only a query that
  // finished with neither data nor an error is a caller with nothing.
  return input.isPending ? "shell" : "signin";
}
